import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Building2, HelpCircle, Trash2 } from "lucide-mui";
import toast from "react-hot-toast";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useUsers } from "../../hooks/resources.ts";
import {
  api,
  getErrorMessage,
  type EnrichMasterFromLabelsResult,
  type FilterOption,
  type Prospect,
  type ProspectFilters,
  type ProspectHitKind,
  type ProspectReviewState,
} from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import { ORG_CRM_READ_SCOPE } from "../../auth/orgRoutes.ts";
import { userHasGlobalScope, userHasOrgScope } from "../../auth/scopes.ts";
import ColumnPickerMenu from "../../components/ColumnPickerMenu.tsx";
import ConfirmDialog from "../../components/ConfirmDialog.tsx";
import CountryName from "../../components/CountryName.tsx";
import EmptyState from "../../components/EmptyState.tsx";
import LiveUpdatesPill from "../../components/LiveUpdatesPill.tsx";
import ExportFieldsDialog from "../../components/ExportFieldsDialog.tsx";
import FormattedDate from "../../components/FormattedDate.tsx";
import StatusChip from "../../components/StatusChip.tsx";
import UserAvatar from "../../components/UserAvatar.tsx";
import DetailPageStatus from "../../components/detail/DetailPageStatus.tsx";
import FilterBar from "../../components/filters/FilterBar.tsx";
import type { FilterSpec } from "../../components/filters/specs.ts";
import { useFilters } from "../../components/filters/useFilters.ts";
import ProspectTable, { type ProspectExtraColumn } from "../../components/prospects/ProspectTable.tsx";
import LabelsCell from "../../components/prospects/collection/LabelsCell.tsx";
import { useCrmStatus } from "../../components/prospects/collection/useCrmStatus.ts";
import { RUN_PARAM, useAgentRunFilter } from "../../components/prospects/useAgentRunFilter.ts";
import { useProspectListView } from "../../components/prospects/useProspectListView.ts";
import ReviewBulkBar from "../../components/review/ReviewBulkBar.tsx";
import { REVIEW_LABELS } from "../../components/review/rules.ts";
import TableCard from "../../components/table/TableCard.tsx";
import UrlTablePagination from "../../components/table/UrlTablePagination.tsx";
import { useAuth } from "../../contexts/AuthContext.tsx";
import { useOrganization } from "../../contexts/OrganizationContext.tsx";
import { collectionPath, useTenant } from "../../contexts/TenantContext.tsx";
import { useFilterOptions } from "../../hooks/useFilterOptions.ts";
import { useUrlPaging, useUrlState } from "../../hooks/useUrlState.ts";
import { formatNumber } from "../../utils/format.ts";
import ProspectDrawer from "../prospects/ProspectDrawer.tsx";
import { PROSPECT_REVIEW_COPY, useProspectReview } from "../prospects/prospectReview.ts";
import type { QueueDecision } from "../prospects/queues.ts";
import AddByNameDialog from "./components/AddByNameDialog.tsx";
import CrmExportDialog from "./components/CrmExportDialog.tsx";
import CrmFunnelSummary from "./components/CrmFunnelSummary.tsx";
import EditListDialog from "./components/EditListDialog.tsx";
import EnrichFromLabelsDialog from "./components/EnrichFromLabelsDialog.tsx";
import ListHeader from "./components/ListHeader.tsx";
import ProspectContactsPanel from "./components/ProspectContactsPanel.tsx";
import { useViewSettingsPersistence } from "./hooks/useViewSettingsPersistence.ts";
import { BASE_COLUMNS, type BaseColumnKey, TABLE_COLUMNS, serverSort } from "./types.ts";

// A pinned label column's filter, in the URL.
const labelParam = (key: string) => `label:${key}`;

const KIND_OPTIONS: { value: string; label: string }[] = [
  { value: "signal", label: "Signals" },
  { value: "agent", label: "Prospecting agents" },
  { value: "manual", label: "Added by hand" },
];
const MIN_HITS_OPTIONS = [
  { value: "2", label: "Seen more than once" },
  { value: "3", label: "3 or more hits" },
  { value: "5", label: "5 or more hits" },
];
const STATE_OPTIONS: { value: ProspectReviewState; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "accepted", label: "Accepted" },
  { value: "disqualified", label: "Disqualified" },
];
const isState = (value: string | null): value is ProspectReviewState =>
  STATE_OPTIONS.some((o) => o.value === value);

/** Whether a decision takes the row out of the entry state the page shows. */
function leavesState(state: ProspectReviewState | null, decision: QueueDecision): boolean {
  if (!state) return false;
  const lands =
    decision.action === "accept"
      ? "accepted"
      : decision.action === "disqualify"
        ? "disqualified"
        : decision.action === "reset"
          ? "pending"
          : null;
  return lands !== null && lands !== state;
}

/**
 * A collection: its header (counts, exports, edits), its companies as the
 * tenant's prospects with their entry here (the inbox's list, filters, bulk
 * bar and drawer, plus the collection's columns), and every person on it.
 */
export default function ProspectListDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { selectedOrgId } = useOrganization();
  const { selectedTenantId, setSelectedTenantId, tenantPath } = useTenant();
  const canDecide = userHasGlobalScope(user, "prospecting:write");
  const canContinue = userHasGlobalScope(user, "signal_rules:write");

  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [crmExportOpen, setCrmExportOpen] = useState(false);
  const [addByNameOpen, setAddByNameOpen] = useState(false);
  const [view, setView] = useState<"companies" | "contacts">("companies");

  const [enrichOpen, setEnrichOpen] = useState(false);
  const [enrichSelected, setEnrichSelected] = useState<Record<string, boolean>>({});
  const [enrichResult, setEnrichResult] = useState<EnrichMasterFromLabelsResult | null>(null);

  const { data: filterOptions } = useFilterOptions();
  const roleTypeOptions: FilterOption[] = (filterOptions?.contact_role_types ?? []).map((rt) => ({
    code: rt.code,
    label: rt.label,
  }));

  // The summary only: the companies come through /prospects, a page at a time.
  const detail = useQuery({
    queryKey: keys.prospectList.detail(id ?? ""),
    queryFn: () => api.getProspectList(id!, { includeEntries: false }),
    enabled: !!id,
    meta: { live: true },
  });
  const list = detail.data;

  const detailTenantId = list?.tenant_id;
  useEffect(() => {
    if (detailTenantId && detailTenantId !== selectedTenantId) setSelectedTenantId(detailTenantId);
  }, [detailTenantId, selectedTenantId, setSelectedTenantId]);

  // The "All contacts" tab's people and their CRM state, once it is open. The
  // backend 403s CRM reads for members without the CRM reader role.
  const contactsQuery = useQuery({
    queryKey: keys.prospectListContactEntry.byList(id ?? ""),
    queryFn: () => api.getProspectContacts(id!),
    enabled: !!id && view === "contacts",
  });
  const canReadCrm = userHasOrgScope(user, selectedOrgId, ORG_CRM_READ_SCOPE);
  const crm = useCrmStatus(id ?? "", canReadCrm && view === "contacts");

  const updateList = useMutation({
    mutationFn: (data: { name?: string; description?: string }) => api.updateProspectList(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.prospectList.detail(id!) });
      queryClient.invalidateQueries({ queryKey: keys.prospectList.all });
      setEditOpen(false);
    },
    // Shown in the edit dialog.
    meta: { suppressErrorToast: true },
  });
  const archiveList = useMutation({
    mutationFn: (archived: boolean) => api.updateProspectList(id!, { archived }),
    onSuccess: (_data, archived) => {
      queryClient.invalidateQueries({ queryKey: keys.prospectList.detail(id!) });
      queryClient.invalidateQueries({ queryKey: keys.prospectList.all });
      toast.success(archived ? "Collection archived" : "Collection restored");
    },
  });
  const deleteList = useMutation({
    mutationFn: () => api.deleteProspectList(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.prospectList.all });
      navigate(tenantPath(collectionPath()));
    },
  });
  const removeEntry = useMutation({
    mutationFn: (entryId: string) => api.removeProspectFromList(id!, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.prospect.all });
      queryClient.invalidateQueries({ queryKey: keys.prospectList.all });
    },
  });

  const usersQuery = useUsers();
  const usersById = useMemo(() => new Map((usersQuery.data ?? []).map((u) => [u.id, u])), [usersQuery.data]);

  const enrichPreview = useQuery({
    queryKey: keys.prospectListEnrichPreview.byList(id),
    queryFn: () => api.getEnrichMasterFromLabelsPreview(id!),
    enabled: !!id,
  });
  const enrichMutation = useMutation({
    mutationFn: (entryIds: string[]) => api.enrichMasterFromLabels(id!, entryIds),
    onSuccess: (data) => {
      setEnrichResult(data);
      queryClient.invalidateQueries({ queryKey: keys.prospectListEnrichPreview.byList(id) });
      queryClient.invalidateQueries({ queryKey: keys.prospect.all });
    },
    // Shown in the enrich dialog.
    meta: { suppressErrorToast: true },
  });

  const saveSettingsMutation = useMutation({
    mutationFn: (view_settings: Parameters<typeof api.updateProspectList>[1]["view_settings"]) =>
      api.updateProspectList(id!, { view_settings }),
  });
  const {
    selectedLabelKeys,
    setSelectedLabelKeys,
    hiddenBaseColumns,
    setHiddenBaseColumns,
    sortKey,
    setSortKey,
    sortDir,
    setSortDir,
  } = useViewSettingsPersistence({ list, onSave: saveSettingsMutation.mutate });
  // The sort is the collection's, saved for everyone with its columns.
  const sort = serverSort(sortKey);
  const dir = sortKey ? sortDir : "desc";

  const url = useUrlState();
  const { searchParams } = url;
  const paging = useUrlPaging(url, 50);
  const stateParam = searchParams.get("state");
  const entryState = isState(stateParam) ? stateParam : null;
  const kindParam = searchParams.get("kind");
  const kinds = kindParam ? (kindParam.split(",").filter(Boolean) as ProspectHitKind[]) : [];
  const countryParam = searchParams.get("country");
  const countries = countryParam ? countryParam.split(",").filter(Boolean) : [];
  const minHits = parseInt(searchParams.get("min_hits") ?? "", 10) || undefined;
  const runFilter = searchParams.get(RUN_PARAM) ?? "";
  const q = searchParams.get("q") ?? "";
  // Only the pinned columns' filters apply: unpinning a column drops its filter.
  const labelFilters = selectedLabelKeys.flatMap((key) => {
    const value = searchParams.get(labelParam(key));
    return value ? [`${key}:${value}`] : [];
  });

  const filters: ProspectFilters = {
    tenant_id: list?.tenant_id ?? "",
    collection_id: id,
    entry_state: entryState ? [entryState] : undefined,
    label: labelFilters.length ? labelFilters : undefined,
    kinds: kinds.length ? kinds : undefined,
    country: countries.length ? countries : undefined,
    min_hits: minHits,
    agent_run_id: runFilter || undefined,
    q: q || undefined,
    sort,
    dir,
  };
  const listView = useProspectListView({
    filters,
    url,
    paging,
    leaves: (decision) => leavesState(entryState, decision),
  });

  const { data: countryCodes = [], isLoading: countriesLoading } = useQuery({
    queryKey: keys.prospect.countries(list?.tenant_id ?? null),
    queryFn: () => api.getProspectCountries(list!.tenant_id),
    enabled: !!list,
    staleTime: 5 * 60_000,
  });
  // The runs that delivered into this collection.
  const runSpec = useAgentRunFilter(list?.tenant_id ?? null, runFilter, id);
  const labelValues = list?.label_values;
  const specs = useMemo<FilterSpec[]>(
    () => [
      {
        kind: "options",
        key: "state",
        label: "Show",
        param: "state",
        multiple: false,
        pinned: true,
        noneLabel: "All",
        options: STATE_OPTIONS,
      },
      { kind: "options", key: "kind", label: "Found by", param: "kind", pinned: true, options: KIND_OPTIONS },
      runSpec,
      {
        kind: "options",
        key: "min_hits",
        label: "Hits",
        param: "min_hits",
        multiple: false,
        noneLabel: "Any",
        options: MIN_HITS_OPTIONS,
      },
      {
        kind: "options",
        key: "country",
        label: "Country",
        param: "country",
        loading: countriesLoading,
        options: countryCodes.map((code) => ({
          value: code,
          label: code,
          icon: <CountryName code={code} showName={false} />,
        })),
        searchPlaceholder: "Search countries",
      },
      // A pill per pinned label column with more than one value to choose from.
      ...selectedLabelKeys.flatMap((key): FilterSpec[] => {
        const values = labelValues?.[key] ?? [];
        if (values.length < 2 && !searchParams.get(labelParam(key))) return [];
        return [
          {
            kind: "options",
            key: labelParam(key),
            label: key,
            param: labelParam(key),
            multiple: false,
            pinned: true,
            noneLabel: "All",
            options: values.map((v) => ({ value: v, label: v })),
          },
        ];
      }),
    ],
    [runSpec, countriesLoading, countryCodes, selectedLabelKeys, labelValues, searchParams],
  );
  const filterBar = useFilters(specs, { state: url, search: { param: "q", placeholder: "Search companies" } });

  if (detail.isLoading || detail.isError || !list) {
    return (
      <DetailPageStatus
        isLoading={detail.isLoading}
        message="Collection not found."
        back={{ to: tenantPath(collectionPath()), label: "Collections" }}
      />
    );
  }

  const allLabelKeys = Object.keys(list.label_values);
  const hidden = (key: BaseColumnKey) => hiddenBaseColumns.includes(key);
  const handleSort = (column: string) => {
    if (sort === column) {
      setSortKey(column);
      setSortDir(dir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(column);
      setSortDir(column === "company" || column.startsWith("label:") ? "asc" : "desc");
    }
    // A new order starts on the first page.
    url.changeFilter({});
  };
  const toggleLabelKey = (key: string) =>
    setSelectedLabelKeys((prev) => {
      const willRemove = prev.includes(key);
      if (willRemove && sort === `label:${key}`) setSortKey(null);
      return willRemove ? prev.filter((k) => k !== key) : [...prev, key];
    });
  const setHiddenColumns = (next: BaseColumnKey[]) => {
    if ((next.includes("contacts") && sort === "contacts") || (next.includes("date_added") && sort === "added")) {
      setSortKey(null);
    }
    setHiddenBaseColumns(next);
  };
  const resetViewSettings = () => {
    setSelectedLabelKeys([]);
    setHiddenBaseColumns([]);
    filterBar.clearAll();
    setSortKey(null);
    setSortDir("desc");
  };

  const addedBy = (p: Prospect) => {
    const entry = p.entry;
    if (!entry) return null;
    if (entry.added_with_method === "agent" || entry.added_by_agent_run_id) return "AI agent";
    if (entry.added_by_user_id) {
      const u = usersById.get(entry.added_by_user_id);
      return (
        <UserAvatar
          displayName={u?.display_name ?? null}
          email={u?.email ?? null}
          pictureUrl={u?.picture_url ?? null}
          size={24}
        />
      );
    }
    return entry.added_with_method === "mcp" ? "MCP" : "Manual";
  };

  // Where the row stands here, when that is not where it stands for the
  // tenant, and what the entry says about itself.
  const entryStatus = (p: Prospect) => {
    const entry = p.entry;
    if (!entry) return null;
    return (
      <>
        {entry.review_state !== p.review_state && (
          <Tooltip title="Its state in this collection">
            <span>
              <StatusChip
                kind="prospect_status"
                value={entry.review_state}
                label={`Here: ${REVIEW_LABELS[entry.review_state]}`}
                variant="outlined"
              />
            </span>
          </Tooltip>
        )}
        {entry.review_state === "disqualified" && entry.disqualification_reason ? (
          <Tooltip title={entry.disqualification_reason}>
            <HelpCircle fontSize="small" sx={{ color: "error.main", opacity: 0.8 }} />
          </Tooltip>
        ) : (
          entry.inclusion_reason && (
            <Tooltip title={entry.inclusion_reason}>
              <HelpCircle fontSize="small" sx={{ color: "text.secondary", opacity: 0.7 }} />
            </Tooltip>
          )
        )}
        {entry.review_state !== "disqualified" && entry.missing_required_properties.length > 0 && (
          <Tooltip title={`Missing required: ${entry.missing_required_properties.join(", ")}`}>
            <AlertTriangle fontSize="small" sx={{ color: "warning.main" }} />
          </Tooltip>
        )}
      </>
    );
  };

  const extraColumns: ProspectExtraColumn[] = [
    ...(hidden("contacts")
      ? []
      : [
          {
            column: { id: "contacts", key: "contacts", label: "Contacts", align: "right" as const, width: 100 },
            render: (p: Prospect) => (
              <Chip
                size="small"
                variant={p.entry?.contact_count ? "filled" : "outlined"}
                color={p.entry?.contact_count ? "primary" : "default"}
                label={formatNumber(p.entry?.contact_count ?? 0)}
                sx={{ height: 20, minWidth: 32 }}
              />
            ),
          },
        ]),
    ...selectedLabelKeys.map((key) => ({
      column: { id: `label:${key}`, key: `label:${key}`, label: key },
      render: (p: Prospect) => p.entry?.labels[key] ?? "-",
    })),
    ...(hidden("labels")
      ? []
      : [
          {
            column: { id: null, key: "labels", label: "Labels" },
            interactive: true,
            render: (p: Prospect) => (
              <LabelsCell
                labels={p.entry?.labels ?? {}}
                selectedLabelKeys={selectedLabelKeys}
                onPin={(k) => setSelectedLabelKeys((prev) => (prev.includes(k) ? prev : [...prev, k]))}
              />
            ),
          },
        ]),
    ...(hidden("added_by")
      ? []
      : [{ column: { id: null, key: "added_by", label: "Added by", width: 100 }, render: addedBy }]),
    ...(hidden("date_added")
      ? []
      : [
          {
            column: { id: "added", key: "date_added", label: "Date added", width: 120 },
            render: (p: Prospect) => p.entry && <FormattedDate value={p.entry.created_at} />,
          },
        ]),
  ];

  const blanks = enrichPreview.data?.blanks ?? [];
  const conflicts = enrichPreview.data?.conflicts ?? [];
  const blocked = enrichPreview.data?.blocked ?? [];
  const openEnrichDialog = () => {
    const initial: Record<string, boolean> = {};
    for (const e of blanks) initial[e.entry_id] = true;
    for (const e of conflicts) initial[e.entry_id] = false;
    setEnrichSelected(initial);
    setEnrichResult(null);
    enrichMutation.reset();
    setEnrichOpen(true);
  };
  const submitEnrich = () => {
    const ids = Object.entries(enrichSelected)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (ids.length) enrichMutation.mutate(ids);
  };

  const total = list.prospect_count;
  const shown = listView.count?.total;

  return (
    <>
      <ListHeader
        list={list}
        enrichableBlanks={blanks.length}
        enrichableConflicts={conflicts.length}
        onEdit={() => {
          setEditName(list.name);
          setEditDesc(list.description ?? "");
          updateList.reset();
          setEditOpen(true);
        }}
        onToggleArchive={() => archiveList.mutate(!list.archived)}
        onDelete={() => setDeleteOpen(true)}
        onOpenEnrich={openEnrichDialog}
        onOpenExport={() => setExportDialogOpen(true)}
        onOpenCrmExport={() => setCrmExportOpen(true)}
        onOpenAddByName={() => setAddByNameOpen(true)}
        exportSlot={
          <>
            <CrmExportDialog open={crmExportOpen} onClose={() => setCrmExportOpen(false)} listId={id!} />
            {exportDialogOpen && (
              <ExportFieldsDialog
                onClose={() => setExportDialogOpen(false)}
                storageKey="export-fields-prospect-list"
                showProspectFields
                roleTypes={roleTypeOptions}
                onExport={async (fields, roleColumns) => {
                  try {
                    await api.exportProspectList(id!, list.name, fields, roleColumns);
                  } catch (err) {
                    toast.error(getErrorMessage(err));
                    throw err;
                  }
                }}
              />
            )}
          </>
        }
      />

      <Tabs
        value={view}
        onChange={(_, v) => setView(v)}
        sx={{ mb: 2, minHeight: 40, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab value="companies" label={`Companies (${formatNumber(total)})`} sx={{ minHeight: 40 }} />
        <Tab
          value="contacts"
          label={contactsQuery.data ? `All contacts (${formatNumber(contactsQuery.data.length)})` : "All contacts"}
          sx={{ minHeight: 40 }}
        />
      </Tabs>

      {view === "contacts" && (
        <>
          <CrmFunnelSummary crm={crm} />
          <ProspectContactsPanel listId={id!} />
        </>
      )}

      {view === "companies" && total === 0 && (
        <EmptyState
          variant="panel"
          icon={<Building2 />}
          title="No companies in this collection yet"
          description="Add companies from the Companies page, or add one the database doesn't hold by name"
          action={
            <Button component={RouterLink} to={tenantPath("/companies")} variant="outlined">
              Browse companies
            </Button>
          }
        />
      )}
      {view === "companies" && total > 0 && (
        <Box>
          <TableCard
            loading={listView.isLoading}
            notice={<LiveUpdatesPill {...listView.live} />}
            toolbar={
              <FilterBar
                filters={filterBar}
                count={
                  shown === undefined
                    ? undefined
                    : filterBar.activeCount > 0
                      ? `${formatNumber(shown)} of ${formatNumber(total)}`
                      : `${formatNumber(shown)} ${shown === 1 ? "company" : "companies"}`
                }
                actions={
                  <ColumnPickerMenu
                    groups={[{ key: "base", label: "Columns", columns: BASE_COLUMNS }]}
                    hidden={hiddenBaseColumns}
                    onChange={setHiddenColumns}
                    defaultHidden={[]}
                    optIn={{
                      label: "Label columns",
                      columns: allLabelKeys,
                      shown: selectedLabelKeys,
                      onToggle: toggleLabelKey,
                    }}
                    footnote={saveSettingsMutation.isPending ? "Saving…" : "Saved for everyone"}
                    onReset={resetViewSettings}
                  />
                }
              />
            }
            footer={<UrlTablePagination total={listView.total} paging={paging} />}
          >
            <ProspectTable
              rows={listView.rows}
              removedIds={listView.removedIds}
              extraColumns={extraColumns}
              statusExtra={entryStatus}
              hiddenColumns={TABLE_COLUMNS.filter((c) => hidden(c))}
              sortBy={sort}
              sortDir={dir}
              onSort={handleSort}
              openId={listView.prospectId}
              highlightedId={listView.highlightedId}
              onOpen={listView.open}
              scrollToRow={listView.scrollToRow}
              selection={listView.selection}
              canDecide={canDecide}
              canContinue={canContinue}
              rowActions={(p) => [
                {
                  label: "Remove from collection",
                  icon: <Trash2 fontSize="small" />,
                  destructive: true,
                  hidden: !p.entry,
                  onClick: () =>
                    removeEntry.mutateAsync(p.entry!.entry_id).then(() => listView.dropRow(p.id)),
                  confirm: {
                    title: `Remove ${p.company_name ?? "this company"} from ${list.name}?`,
                    body: "The company stays in the database and on its other collections; its people on this collection go with it.",
                    confirmLabel: "Remove",
                  },
                },
              ]}
              emptyCopy={filterBar.activeCount ? "No companies match your filters" : "No companies here yet"}
            />
          </TableCard>
        </Box>
      )}

      {canDecide && (
        <ReviewBulkBar
          selected={listView.selectedProspects}
          onClear={listView.selection.clear}
          useReview={useProspectReview}
          copy={PROSPECT_REVIEW_COPY}
          canContinue={canContinue}
        />
      )}

      <ProspectDrawer
        prospectId={listView.prospectId}
        onClose={listView.close}
        contactId={listView.contactId}
        onOpenContact={listView.openContact}
        onCloseContact={listView.closeContact}
        canDecide={canDecide}
        canContinue={canContinue}
        onReviewed={listView.onReviewed}
        onStep={listView.onStep}
        collectionId={id}
      />

      <AddByNameDialog listId={id!} open={addByNameOpen} onClose={() => setAddByNameOpen(false)} />

      <EditListDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        name={editName}
        description={editDesc}
        onNameChange={setEditName}
        onDescriptionChange={setEditDesc}
        onSave={() => updateList.mutate({ name: editName, description: editDesc })}
        saving={updateList.isPending}
        error={updateList.error ? getErrorMessage(updateList.error) : null}
      />

      <ConfirmDialog
        open={deleteOpen}
        title="Delete collection"
        pending={deleteList.isPending}
        onConfirm={() => deleteList.mutate()}
        onClose={() => setDeleteOpen(false)}
      >
        Delete "{list.name}" and all its prospect entries? This cannot be undone.
      </ConfirmDialog>

      <EnrichFromLabelsDialog
        open={enrichOpen}
        onClose={() => {
          setEnrichOpen(false);
          setEnrichResult(null);
        }}
        blanks={blanks}
        conflicts={conflicts}
        blocked={blocked}
        selected={enrichSelected}
        onToggle={(entryId, checked) => setEnrichSelected((s) => ({ ...s, [entryId]: checked }))}
        onSubmit={submitEnrich}
        result={enrichResult}
        submitting={enrichMutation.isPending}
        error={enrichMutation.error ? getErrorMessage(enrichMutation.error) : null}
      />
    </>
  );
}
